import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import Cookies from 'js-cookie';

import { AUTHENTICATION_TOKEN, KEYS_QUERY } from 'common/constants';
import useService from 'common/hook/useService';
import { deriveSchoolYear } from 'common/utils/schoolYearMonths';
import type { PlanningPeriod } from 'common/types/planningPeriod';

/**
 * Kỳ kinh doanh (năm học) hiện hành.
 *
 * Nguồn chuẩn là bảng Kỳ kinh doanh của KIOS, đúng như tài liệu GANET 2.6 yêu cầu — GATE không có
 * combo chọn năm học, chỉ lấy kỳ `isCurrent`. Nhờ đó năm học theo đúng cấu hình của KIOS thay vì
 * quy tắc cứng "từ tháng 9": KIOS cho đặt ngày bắt đầu/kết thúc kỳ, có năm không rơi vào 01/09.
 *
 * KIOS hỏng thì rơi về suy từ lịch hệ thống — mất năm học không được làm chết cả ô chọn tháng.
 */
const useCurrentPlanningPeriod = () => {
  const service = useService();

  const query = useQuery({
    queryKey: [KEYS_QUERY.planningPeriods],
    queryFn: async (): Promise<PlanningPeriod[]> => {
      if (!service) return [];
      return (await service.KiosController_getPlanningPeriods()) ?? [];
    },
    // FE không cache; chặn gọi trùng để cache 1 phút của BE lo.
    staleTime: 0,
    // Hook này bị gọi từ sidebar — thứ luôn mounted. Nếu bắn khi CHƯA có token thì BE trả 401,
    // interceptor gọi handler logout, và app quay lại /login thành vòng lặp. Chỉ hỏi khi đã có
    // token (kể cả token khách).
    enabled: !!service && !!Cookies.get(AUTHENTICATION_TOKEN),
    // Đây là dữ liệu phụ trợ (nhãn năm học + danh sách tháng): hỏng thì im lặng dùng đường lùi,
    // đừng thử lại dồn dập.
    retry: false,
  });

  const current = (query.data ?? []).find((p) => p.isCurrent);
  const kiosYear = Number(current?.year);
  const hasKiosYear = Number.isInteger(kiosYear) && kiosYear > 0;

  const today = dayjs();
  const schoolYear = hasKiosYear ? kiosYear : deriveSchoolYear(today.year(), today.month() + 1);

  return {
    ...query,
    /** Năm học hiện hành: của KIOS nếu lấy được, không thì suy từ lịch hệ thống. */
    schoolYear,
    /** true khi năm học đang là số suy ra, chưa được KIOS xác nhận. */
    isFallbackYear: !hasKiosYear,
    /**
     * Nhãn hiển thị "2026 - 2027". Cố tình KHÔNG dùng `name` của KIOS ("N 2026 - 2027") vì chỗ
     * hiển thị đã có chữ "Năm học" đứng trước, ghép vào thành "Năm học N 2026 - 2027".
     */
    schoolYearLabel: `${schoolYear} - ${schoolYear + 1}`,
    /** Tên kỳ do KIOS đặt, giữ nguyên văn — dùng khi cần đối chiếu với màn hình KIOS. */
    periodName: current?.name,
  };
};

export default useCurrentPlanningPeriod;
